import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManRadioButtonGroup, ManRadioButton, ManText } from '@craft-design-system/core-components-react';
import useGraphRadio from '../../utils/useGraphRadio';
import Shimmer_graph from '../shimmer_graph';
import { Card } from 'react-bootstrap';
import PopoverComponent from '../popover';
import AverageDisplay from './Average_value';
import SegmentedControl from './SegmentedControl';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Zoom from '../Zoom';
import DownloadIcon from '@mui/icons-material/Download';
import { IconButton } from '@mui/material';
import { useFilterContext } from '../FilterContext';
import AIToggleButton from '../AIToggleButton';

const NetEnergyConsumption = ({ value }) => {
  const [netenergyconsumption_graph, set_netenergyconsumption_graph] = useState(null);
  const radio_query = useGraphRadio();
  const [radio_buttons_test, set_Radio_values] = useState(null);
  const { filterdata } = useFilterContext();
  const { startDate, endDate } = filterdata || {};
  const [downloadData, setDownloadData] = useState(JSON.parse(value.netEnergyConsumption[3]));
  const [isLoading, setIsLoading] = useState(false);
  const [avg_value, setAvg_value] = useState(value.netEnergyConsumption[2]?.['average'] || null);
  const [chartType, setChartType] = useState("Bar");
  const { fetchSegmentedChart } = useSegmentedChartQuery();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Handle radio button changes
  const handleSelectChange1 = (event) => {
    console.log('Radio button changed:', event.detail.name, event.detail.value);
    const newRadioValues = {
      ...radio_buttons_test,
      [event.detail.name]: event.detail.value,
    };
    console.log('Setting radio values to:', newRadioValues);
    set_Radio_values(newRadioValues);
  };

  const handleDownloadClick = () => {
    if (!Array.isArray(downloadData) || downloadData.length === 0 || !downloadData[0] || Object.keys(downloadData[0]).length === 0) {
      setShowModal(true);
      return;
    }
    const csvContent = convertToCSV(downloadData);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'net_energy_consumption_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const convertToCSV = (data) => {
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row =>
      Object.values(row)
        .map(value => {
          const stringValue = String(value);
          if (/[",\n]/.test(stringValue)) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(',')
    );
    return [headers, ...rows].join('\n');
  };


  // Handle segmented chart fetch
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: "Net Energy Consumption",
        });

        console.log("📊 Segmented chart response:", data);
        
        if (chartType === "Bar") {
          set_netenergyconsumption_graph(JSON.parse(data.graph_data[0]));
          setDownloadData(JSON.parse(data.graph_data[3]));
        } else {
          set_netenergyconsumption_graph(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[1]));
        }
      } catch (error) {
        console.error("❌ Error fetching chart data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad && chartType && value?.vin_short) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType]);

  

  // Handle radio button fetch
  useEffect(() => {
    if (radio_buttons_test) {
      console.log("🔄 Radio button changed:", radio_buttons_test);

      setIsLoading(true);

      const requestParams = {
        vins_short: [value.vin_short],
        radio_value: radio_buttons_test,
        columnname: "Net Energy Consumption",
        startDate,
        endDate,
        filterdata: filterdata,
        resetdata: filterdata?.resetdata
      };

      console.log("📡 Fetching graph data with:", requestParams);

      radio_query(requestParams)
        .then(data => {
          console.log("✅ Response from radio_query:", data);

          try {
            const graphData = JSON.parse(data.jsonData.graph_data[0]);
            const avgVal = data.jsonData.graph_data[2]?.average;

            set_netenergyconsumption_graph(graphData);
            setAvg_value(avgVal);
            
            console.log("📊 Graph updated:", graphData);
            console.log("📈 Average value:", avgVal);
          } catch (err) {
            console.error("❌ Error parsing graph_data:", err);
          }
        })
        .catch(error => {
          console.error("❌ Error in radio_query:", error);
        })
        .finally(() => {
          console.log("⏳ Finished radio_query fetch cycle");
          setIsLoading(false);
        });
    }
  }, [radio_buttons_test]);

  // Set initial graph data from value prop
  useEffect(() => {
    try {
      set_netenergyconsumption_graph(JSON.parse(value.netEnergyConsumption[0]));
    } catch (error) {
      console.error("Error parsing fallback netEnergyConsumption JSON", error);
    }
  }, [value.netEnergyConsumption]);
  
  return (
    <Card className='card_ride'>
      {/* Title and Info Icon */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '8px'
      }}>
        <div style={{
          flex: 1,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
            style={{ lineHeight: 'normal' }}
          >
           Net energy consumption
          </ManText>
        </div>
        <div style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center', // Ensure icon aligns with text
                  height: '100%', // Match height to parent for consistent alignment
                  flexDirection:"row-reverse"
        }}>
          <PopoverComponent
            id="netenergyconsumption"
            content="<b>Net Energy Consumption:</b> The net energy consumption is the difference of energy consumption and recuperated energy.<br/><br/><b>Telematic Raw Data:</b><br/>• TBM3: payloaddecoded_totalenergyconsumption_value, payloaddecoded_recuperatedenergy_value<br/>• CM4: payload_electricmessagesystem_totalenergyconsumption, payload_electricmessagesystem_recuperatedenergy (BBP), payload_electricmessagesystem_batrecenergycbp (CBP)<br/><br/><b>Time Series Data:</b><br/>If multiple vehicles are selected average values are displayed. Days without data are not taken into account for the calculation.<br/><br/><b>Histogram:</b><br/>Each bar represents the number of days that fall within a certain range (bin) of <b>net energy consumption values</b>. All daily values since start of data acquisition for the selected vehicle(s) are visualized per default.<br/><br/><b>Boxplot:</b><br/>The boxplot shows the <b>distribution of daily net energy consumption values</b> for the selected vehicle(s) since start of data acquisition. If multiple vehicles are selected, the boxplot reflects the <b>combined distribution</b> of all their daily values.<br/>• The <b>box</b> represents the <b>middle 50%</b> of values (from the 25th percentile ('q1') to the 75th percentile ('q3'))<br/>• The <b>line inside the box</b> shows the <b>median</b> (50th percentile)<br/>• The whiskers extend to show the range of typical values ('min' and 'max') and visualize <b>the spread of the data</b> beyond the central box."
            style={{ verticalAlign: 'middle' }}
          />
              <Zoom
              chartData={netenergyconsumption_graph?.data}
              chartLayout={netenergyconsumption_graph?.layout}
              chartTitle="Net energy consumption"
            />
    <IconButton onClick={handleDownloadClick} style={{ color: 'black' }}>
            <DownloadIcon />
          </IconButton>
        </div>
      </div>

      {/* Controls: Average value, Radio buttons, Segmented Control */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '20px',
        marginTop: '16px',
      }}>
        {/* Left: Average value (only for Bar) */}
        {chartType === 'Bar' ? (
          <div style={{ flexShrink: 0 }}>
            <AverageDisplay averages={avg_value} unit="kWh/100km" />
          </div>
        ) : (
          <div style={{ width: '150px' }} />
        )}

        {/* Center: Radio Buttons (only for Bar) */}
        {chartType === 'Bar' ? (
          <ManRadioButtonGroup
            name="Net Energy Consumption"
            invalid="false"
            onManChange={handleSelectChange1}
            style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
          >
            <ManRadioButton
              name="Net Energy Consumption"
              label="Daily"
              value="Daily"
              checked
            />
            <ManRadioButton
              name="Net Energy Consumption"
              label="Monthly"
              value="Monthly"
            />
            <ManRadioButton
              name="Net Energy Consumption"
              label="Yearly"
              value="Yearly"
            />
          </ManRadioButtonGroup>
        ) : (
          <div style={{ flexGrow: 1 }} />
        )}

        {/* Right: Segmented Control */}
        <div style={{ flexShrink: 0 }}>
          <SegmentedControl selected={chartType} onChange={(type) => setChartType(type)} />
        </div>
      </div>

      {/* Chart and Text */}
      {isLoading ? (
        <Shimmer_graph />
      ) : (
        netenergyconsumption_graph && (
          <>
            <Plot
              data={netenergyconsumption_graph.data}
              layout={{
                ...netenergyconsumption_graph.layout,
                autosize: true,
                responsive: true,
                margin: chartType === 'Bar' 
                  ? { t: 10, r: 10, l: 10, b: 130 }
                  : { t: 10, r: 10, l: 10, b: 160 }
              }}
              useResizeHandler={true}
            />

            {/* 🔥 AI Toggle Button - Only show for Bar charts with data */}
            {chartType === 'Bar' && downloadData?.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                paddingLeft: '10px',
                marginTop: '8px',
                marginBottom: '8px'
              }}>
                <AIToggleButton
                  downloadData={downloadData}
                  radioButtonState={radio_buttons_test || { 'Net Energy Consumption': 'Daily' }}
                  kpiName="Net Energy Consumption"
                  chartType={chartType}
                  vinShort={value.vin_short}
                  title="AI | Automotive Intelligence - Net Energy Consumption Analysis"
                />
              </div>
            )}
          </>
        )
      )}
    </Card>
  );
};

NetEnergyConsumption.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.string.isRequired,
    netEnergyConsumption: PropTypes.array.isRequired,
  }).isRequired,
};

export default NetEnergyConsumption;
